import { useMemo } from 'react';
import { useSelector } from '@xstate/store/react';
import { useCanvasStores } from '../stores/CanvasContext';
import { computeAncestors } from '../lib/computeAncestors';
import { getConnectorData } from '../entityTypes';

export interface DependencyHighlight {
  isActive: boolean;
  focusId: string | null;
  highlightedEntityIds: Set<string> | null;
  highlightedConnectorIds: Set<string> | null;
}

const INACTIVE: DependencyHighlight = {
  isActive: false,
  focusId: null,
  highlightedEntityIds: null,
  highlightedConnectorIds: null,
};


export function useDependencyExplorerHighlight(): DependencyHighlight {
  const { store, uiStore } = useCanvasStores();
  const isActive = useSelector(uiStore, s => s.context.depExplorerActive);
  const focusId = useSelector(uiStore, s => s.context.depExplorerFocusId);

  // Build topology key directly in selector — stable during drag (only connector endpoints matter).
  // Avoids creating new rawEntities array on every nodes change.
  const topoKey = useSelector(store, s => {
    const connectors = Object.values(s.context.connectors);
    let k = '';
    for (const e of connectors) {
      const cd = getConnectorData(e);
      k += `${e.id}:${cd.from.entityId}>${cd.to.entityId}|`;
    }
    return k;
  });

  return useMemo(() => {
    if (!isActive) return INACTIVE;
    if (!focusId) return { isActive: true, focusId: null, highlightedEntityIds: null, highlightedConnectorIds: null };

    const ctx = store.getSnapshot().context;
    const rawEntities = [
      ...Object.values(ctx.nodes),
      ...Object.values(ctx.connectors),
    ];
    const { ancestorEntityIds, pathConnectorIds } = computeAncestors(rawEntities, focusId);
    return {
      isActive: true,
      focusId,
      highlightedEntityIds: ancestorEntityIds,
      highlightedConnectorIds: pathConnectorIds,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topoKey, isActive, focusId]);
}
